import React, { useState } from 'react';
import { Star, Bookmark, Trash2, Search, Filter, Grid, List } from 'lucide-react';
import { useNavigation } from './NavigationBridge';
import { SelectField } from "components/ui/select-field";

interface FavoritesManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FavoritesManager: React.FC<FavoritesManagerProps> = ({ isOpen, onClose }) => {
  const { state, navigateTo, removeFromFavorites, addToRecent } = useNavigation();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'template' | 'test' | 'agent' | 'campaign'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [_editingId, _setEditingId] = useState<string | null>(null);

  const filteredFavorites = state.favorites.filter(fav => {
    const matchesSearch = fav.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterType === 'all' || fav.type === filterType;
    return matchesSearch && matchesFilter;
  });

  const handleNavigateToFavorite = (favorite: any) => {
    navigateTo(favorite.path, favorite.context);
    addToRecent({
      type: favorite.type,
      name: favorite.name,
      path: favorite.path,
      context: favorite.context
    });
    onClose();
  };

  const handleRemoveFavorite = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    removeFromFavorites(id);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'template':
        return <div className="w-3 h-3 bg-green-500 rounded-full" />;
      case 'test':
        return <div className="w-3 h-3 bg-blue-500 rounded-full" />;
      case 'agent':
        return <div className="w-3 h-3 bg-purple-500 rounded-full" />;
      case 'campaign':
        return <div className="w-3 h-3 bg-orange-500 rounded-full" />;
      default:
        return <div className="w-3 h-3 bg-gray-500 rounded-full" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface rounded-lg shadow-2xl w-full max-w-4xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-line">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Star className="w-6 h-6 text-yellow-500 dark:text-yellow-400" />
              <h2 className="text-xl font-semibold text-content">Favorites & Bookmarks</h2>
            </div>
            <button
              onClick={onClose}
              className="text-content-subtle hover:text-content-secondary transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Search and Filters */}
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-content-subtle" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search favorites..."
                className="w-full pl-10 pr-4 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-content-subtle" />
              <SelectField
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as any)}
                className="border border-line-strong rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Types</option>
                <option value="template">Templates</option>
                <option value="test">Tests</option>
                <option value="agent">Agents</option>
                <option value="campaign">Campaigns</option>
              </SelectField>
            </div>

            <div className="flex items-center border border-line-strong rounded-lg">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300' : 'text-content-subtle hover:text-content-secondary'}`}
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300' : 'text-content-subtle hover:text-content-secondary'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredFavorites.length === 0 ? (
            <div className="text-center py-12">
              <Star className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-content mb-2">No favorites found</h3>
              <p className="text-content-muted">
                {searchQuery || filterType !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Start adding items to your favorites to see them here.'}
              </p>
            </div>
          ) : (
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-3'}>
              {filteredFavorites.map((favorite) => (
                <div
                  key={favorite.id}
                  className={`
                    bg-surface border border-line rounded-lg p-4 hover:shadow-md transition-all cursor-pointer
                    ${viewMode === 'grid' ? 'h-32' : 'flex items-center space-x-4'}
                  `}
                  onClick={() => handleNavigateToFavorite(favorite)}
                >
                  {viewMode === 'grid' ? (
                    <div className="h-full flex flex-col">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          {getTypeIcon(favorite.type)}
                          <span className="text-xs text-content-muted capitalize">{favorite.type}</span>
                        </div>
                        <button
                          onClick={(e) => handleRemoveFavorite(favorite.id, e)}
                          className="text-content-subtle hover:text-red-500 dark:hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <h3 className="font-medium text-content mb-1 truncate">{favorite.name}</h3>
                      <p className="text-xs text-content-muted mb-2">Added {formatDate(favorite.addedAt)}</p>
                      <div className="mt-auto">
                        <div className="flex items-center space-x-1 text-xs text-content-subtle">
                          <Bookmark className="w-3 h-3" />
                          <span>Favorite</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center space-x-3">
                        {getTypeIcon(favorite.type)}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-content truncate">{favorite.name}</h3>
                          <div className="flex items-center space-x-4 text-sm text-content-muted">
                            <span className="capitalize">{favorite.type}</span>
                            <span>Added {formatDate(favorite.addedAt)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Star className="w-4 h-4 text-yellow-500 dark:text-yellow-400" />
                        <button
                          onClick={(e) => handleRemoveFavorite(favorite.id, e)}
                          className="text-content-subtle hover:text-red-500 dark:hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-line bg-gray-50 dark:bg-dark-700 rounded-b-lg">
          <div className="flex items-center justify-between text-sm text-content-secondary">
            <span>{filteredFavorites.length} favorite{filteredFavorites.length !== 1 ? 's' : ''}</span>
            <div className="flex items-center space-x-4">
              <span>Click to navigate • Star icon to remove</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Favorite button component for adding items to favorites
export const FavoriteButton: React.FC<{
  item: {
    type: 'template' | 'test' | 'agent' | 'campaign';
    name: string;
    path: string;
    context?: Record<string, any>;
  };
  className?: string;
}> = ({ item, className = '' }) => {
  const { state, addToFavorites, removeFromFavorites } = useNavigation();
  
  const isFavorited = state.favorites.some(fav => 
    fav.type === item.type && fav.name === item.name && fav.path === item.path
  );

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isFavorited) {
      const favoriteToRemove = state.favorites.find(fav => 
        fav.type === item.type && fav.name === item.name && fav.path === item.path
      );
      if (favoriteToRemove) {
        removeFromFavorites(favoriteToRemove.id);
      }
    } else {
      addToFavorites({
        type: item.type,
        name: item.name,
        path: item.path,
        context: item.context || {}
      });
    }
  };

  return (
    <button
      onClick={handleToggleFavorite}
      className={`transition-colors ${className} ${
        isFavorited 
          ? 'text-yellow-500 dark:text-yellow-400 hover:text-yellow-600 dark:hover:text-yellow-300' 
          : 'text-content-subtle hover:text-yellow-500'
      }`}
      title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
    >
      <Star className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
    </button>
  );
};